import { askConfirm, showAlert, showError, showPrompt } from "@hereby/components";
import type { ChatRecoveryNotificationInput } from "@hereby/core";
import {
  buildMessageAad,
  decryptFile,
  decryptMessage,
  type EncryptedAttachmentRef,
  exportPublicKeySpki,
  fingerprintPublicKey,
  fromBase64Url,
  openRecoveryHandoff,
  sha256Hex,
} from "@hereby/core/chat-crypto";
// 런타임 enum 은 ESM 서브패스에서 — 루트(dist/index.js, CJS)는 vite 가 이름 내보내기를 못 읽는다
import { ChatRecoveryReasonKind, ChatRecoveryStatus } from "@hereby/core/enums";
import type { HerebyClient } from "@hereby/sdk";
import Alpine from "alpinejs";

/**
 * 「대화 복구」 페이지 (MED-927 E11, 확정본 §복구 절차) — 병원 쪽.
 *
 * 조직 시스템 관리자만 본다. 요청(근거 문서 + 범위) → 다른 관리자 승인 → [hereby 운영자 2인 승인·해제] →
 * 이 브라우저가 풀린 메시지 키 묶음(handoff)을 받아 암호문을 풀고 내보낸 뒤 완료(고지 여부)를 보고한다.
 *
 * 키는 서버에 가지 않는다: 요청을 만들 때 이 브라우저가 요청용 ECDH 쌍을 만들어 공개키만 보내고, 비밀키는
 * IndexedDB(`hereby-chat-recovery`)에 추출 불가(non-extractable)로 둔다. handoff 는 그 공개키로만 감싸져
 * 오므로 **요청을 만든 브라우저에서만** 풀 수 있다. 풀린 메시지 키·본문은 메모리에만 있고 화면을 닫으면 사라진다
 * (handoff 는 서버가 한 번만 내준다).
 */

export interface ChatRecoveryDocument {
  storagePath: string;
  name: string;
  size: number;
  sha256: string;
}

export interface ChatRecoveryScope {
  employeeIds?: string[];
  conversationIds?: string[];
  /** 대화방을 고를 때 출발한 당사자(1~2명) — 표시용 */
  subjectEmployeeIds?: string[];
  from?: string;
  to?: string;
}

/** 서버 요약 한 방 — 본문 없음(종류·참여자·기간 안 암호화 메시지 수) */
export interface RecoveryConversationSummary {
  conversationId: string;
  type: string;
  title: string | null;
  hasEncryptedTitle: boolean;
  participants: { employeeId: string; name: string; leftAt: string | null }[];
  participantCount: number;
  messageCount: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  subjects?: { employeeId: string; joinedAt: string | null; leftAt: string | null }[];
}

export const CONVERSATION_TYPE_LABELS: Record<string, string> = {
  direct: "1:1",
  group: "그룹",
  department: "팀방",
  channel: "채널",
  workflow: "공식 안내",
  self: "나와의 대화",
};

/** 방 한 줄 이름 — 제목이 있으면 제목, 없으면 참여자 이름(당사자 제외 우선). 암호화된 이름은 서버가 모른다 */
export function conversationLabel(
  c: RecoveryConversationSummary,
  subjectIds: string[] = [],
): string {
  if (c.title) {
    return c.title;
  }
  const others = c.participants.filter((p) => !subjectIds.includes(p.employeeId));
  const pool = others.length ? others : c.participants;
  const names = pool.map((p) => p.name).filter(Boolean);
  const shown = names.slice(0, 4).join(", ");
  // 당사자를 뺀 만큼은 「외 N명」에도 세지 않는다
  const excluded = c.participants.length - pool.length;
  const rest = c.participantCount - excluded - Math.min(4, names.length);
  const label = shown + (rest > 0 ? ` 외 ${rest}명` : "");
  return label || `대화방 ${c.conversationId.slice(0, 8)}`;
}

/**
 * 요청 폼 → 범위. 돌려주는 값이 문자열이면 사용자에게 보일 오류.
 * - 직원 기준: 그 직원들이 참여한 모든 대화(법적 요청만)
 * - 대화방 고르기: 당사자 1~2명에서 출발해 고른 방들(점검은 정확히 1개)
 */
export function buildRecoveryScope(
  form: {
    scopeMode: "employees" | "rooms";
    employees: { id: string }[];
    selectedRoomIds: string[];
    from: string;
    to: string;
  },
  isLegal: boolean,
): ChatRecoveryScope | string {
  const scope: ChatRecoveryScope = {
    from: dateInputToIso(form.from, false),
    to: dateInputToIso(form.to, true),
  };
  if (form.scopeMode === "employees") {
    if (!isLegal) {
      return "내부 점검은 대화방 1개만 지정할 수 있어요.";
    }
    if (form.employees.length === 0) {
      return "대상 직원을 한 명 이상 골라 주세요.";
    }
    scope.employeeIds = form.employees.map((e) => e.id);
    return scope;
  }
  if (form.employees.length === 0 || form.employees.length > 2) {
    return "당사자를 1~2명 고른 뒤 대화방을 불러와 주세요.";
  }
  if (form.selectedRoomIds.length === 0) {
    return "복구할 대화방을 골라 주세요.";
  }
  if (!isLegal && form.selectedRoomIds.length !== 1) {
    return "내부 점검은 대화방 1개만 지정할 수 있어요.";
  }
  scope.conversationIds = [...form.selectedRoomIds];
  scope.subjectEmployeeIds = form.employees.map((e) => e.id);
  return scope;
}

export interface ChatRecoveryRow {
  id: string;
  status: string;
  reasonKind: string;
  reasonText: string;
  documents: ChatRecoveryDocument[];
  scope: ChatRecoveryScope;
  messageCountInScope: number | null;
  requesterId: string;
  requesterName?: string;
  /** 「누구의 대화」 — 목록·상세가 보여 준다 */
  subjectNames?: string[];
  /** 상세(get)만 — 범위의 방 목록(본문 없음) */
  conversations?: RecoveryConversationSummary[];
  recipientFingerprint: string;
  hospitalApproverId: string | null;
  hospitalApproverName?: string | null;
  hospitalApprovedAt: string | Date | null;
  rejectReason: string | null;
  operatorApprovals: Array<{ at: string | Date; operatorName: string | null }>;
  releasedAt: string | Date | null;
  handoffReady: boolean;
  handoffClaimedAt: string | Date | null;
  exportHash: string | null;
  messageCount: number | null;
  decryptedCount: number | null;
  notification: Record<string, unknown> | null;
  completedAt: string | Date | null;
  expiresAt: string | Date | null;
  createdAt: string | Date;
}

export interface ChatRecoveryAuditRow {
  seq: number;
  step: string;
  actorKind: string;
  actorId: string;
  detail: Record<string, unknown>;
  createdAt: string | Date;
  hash: string;
}

/** 서버가 내려주는 암호문 한 줄 */
interface CipherRow {
  id: string;
  conversationId: string;
  conversationTitle: string;
  conversationType: string | null;
  senderId: string | null;
  senderName: string;
  senderNumber: string;
  createdAt: string | Date;
  deletedAt: string | Date | null;
  type: string;
  keyEpoch: number | null;
  ciphertext: string | null;
  attachments: Array<{ storagePath: string; size: number }>;
}

/** 풀린(또는 못 푼) 메시지 한 줄 — 화면·내보내기 공통 */
export interface RecoveredMessage {
  id: string;
  conversationId: string;
  conversationTitle: string;
  senderId: string | null;
  senderName: string;
  senderNumber: string;
  createdAt: string;
  deletedAt: string | null;
  text: string | null;
  /** 못 푼 이유(키 없음 · 바인딩 불일치 등). null 이면 풀림 */
  error: string | null;
  attachments: EncryptedAttachmentRef[];
}

interface EmployeeCandidate {
  id: string;
  name: string;
  employeeNumber: string;
}

interface WorkState {
  request: ChatRecoveryRow;
  organizationId: string;
  messages: RecoveredMessage[];
  total: number;
  loaded: number;
  decrypted: number;
  failed: number;
  loading: boolean;
  exportHash: string | null;
  exportedAt: string | null;
}

export const STATUS_LABELS: Record<string, string> = {
  [ChatRecoveryStatus.REQUESTED]: "승인 대기",
  [ChatRecoveryStatus.HOSPITAL_APPROVED]: "hereby 심사 중",
  [ChatRecoveryStatus.OPERATOR_APPROVED]: "키 준비 중",
  [ChatRecoveryStatus.RELEASED]: "복호 가능",
  [ChatRecoveryStatus.COMPLETED]: "완료",
  [ChatRecoveryStatus.REJECTED]: "반려",
  [ChatRecoveryStatus.EXPIRED]: "만료",
};

// ─── 상세 팝업 「진행」 단계(2026-10-19 C안 — 오른쪽 타임라인) ─────────────────
export type RecoveryStepState = "done" | "current" | "pending" | "fail";
export interface RecoveryStep {
  title: string;
  sub: string;
  at: string | Date | null;
  state: RecoveryStepState;
}

/**
 * 요청 → 병원 승인 → hereby 심사(2인) → 키 전달 · 수령 → 복호 · 완료 다섯 단계를 행의 사실 값으로 그린다.
 * 반려는 그 단계에서 끝(fail)나고 뒤 단계는 그리지 않는다. 만료는 마지막 단계를 fail 로.
 */
export function recoveryProgressSteps(row: ChatRecoveryRow): RecoveryStep[] {
  const st = row.status;
  const ops = row.operatorApprovals ?? [];
  const docs = row.documents?.length ?? 0;
  const steps: RecoveryStep[] = [
    {
      title: "요청",
      sub: `${row.requesterName || "-"}${docs ? ` · 근거 문서 ${docs}개` : ""}`,
      at: row.createdAt,
      state: "done",
    },
  ];
  // 병원 승인
  if (row.hospitalApprovedAt) {
    steps.push({
      title: "병원 승인",
      sub: row.hospitalApproverName || "-",
      at: row.hospitalApprovedAt,
      state: "done",
    });
  } else if (st === ChatRecoveryStatus.REJECTED) {
    steps.push({ title: "병원에서 반려", sub: row.rejectReason || "", at: null, state: "fail" });
    return steps;
  } else {
    steps.push({
      title: "병원 승인",
      sub: "다른 관리자의 승인을 기다려요",
      at: null,
      state: st === ChatRecoveryStatus.REQUESTED ? "current" : "pending",
    });
  }
  // hereby 심사(2인)
  const opNames = ops.map((a) => a.operatorName || "hereby 운영자").join(" · ");
  if (row.releasedAt || ops.length >= 2) {
    steps.push({
      title: "hereby 심사 (2인)",
      sub: opNames,
      at: ops[ops.length - 1]?.at ?? null,
      state: "done",
    });
  } else if (st === ChatRecoveryStatus.REJECTED) {
    steps.push({
      title: "hereby 심사에서 반려",
      sub: row.rejectReason || "",
      at: null,
      state: "fail",
    });
    return steps;
  } else {
    steps.push({
      title: "hereby 심사 (2인)",
      sub: ops.length ? `${opNames} 승인 · 1명 더 필요` : "",
      at: null,
      state: st === ChatRecoveryStatus.HOSPITAL_APPROVED ? "current" : "pending",
    });
  }
  // 키 전달 · 수령
  if (row.releasedAt) {
    steps.push({
      title: "키 전달 · 수령",
      sub: row.handoffClaimedAt ? "이 브라우저가 받음" : "아직 받지 않음",
      at: row.handoffClaimedAt || row.releasedAt,
      state: "done",
    });
  } else {
    steps.push({
      title: "키 전달 · 수령",
      sub: st === ChatRecoveryStatus.OPERATOR_APPROVED ? "키를 준비하고 있어요" : "",
      at: null,
      state: st === ChatRecoveryStatus.OPERATOR_APPROVED ? "current" : "pending",
    });
  }
  // 복호 · 완료
  if (row.completedAt) {
    steps.push({
      title: "복호 · 완료",
      sub:
        row.messageCount !== null
          ? `메시지 ${row.messageCount}건 중 ${row.decryptedCount ?? 0}건 풀림`
          : "",
      at: row.completedAt,
      state: "done",
    });
  } else if (st === ChatRecoveryStatus.EXPIRED) {
    steps.push({
      title: "만료",
      sub: "기한 안에 복호하지 않았어요",
      at: row.expiresAt,
      state: "fail",
    });
  } else {
    steps.push({
      title: "복호 · 완료",
      sub: st === ChatRecoveryStatus.RELEASED ? "「복호 시작」으로 메시지를 풀어요" : "",
      at: null,
      state: st === ChatRecoveryStatus.RELEASED ? "current" : "pending",
    });
  }
  return steps;
}

export const STATUS_TONES: Record<string, string> = {
  [ChatRecoveryStatus.REQUESTED]: "ds-label--yellow",
  [ChatRecoveryStatus.HOSPITAL_APPROVED]: "ds-label--blue",
  [ChatRecoveryStatus.OPERATOR_APPROVED]: "ds-label--blue",
  [ChatRecoveryStatus.RELEASED]: "ds-label--green",
  [ChatRecoveryStatus.COMPLETED]: "ds-label--gray",
  [ChatRecoveryStatus.REJECTED]: "ds-label--red",
  [ChatRecoveryStatus.EXPIRED]: "ds-label--gray",
};

export const REASON_LABELS: Record<string, string> = {
  [ChatRecoveryReasonKind.LEGAL_REQUEST]: "법원·수사기관 요청",
  [ChatRecoveryReasonKind.AUDIT_CHECK]: "내부 점검",
};

export const STEP_LABELS: Record<string, string> = {
  created: "요청 생성",
  hospital_approved: "병원 승인",
  hospital_rejected: "병원 반려",
  document_viewed: "근거 문서 열람",
  operator_approved: "hereby 운영자 승인",
  envelopes_read: "복구 봉투 읽음",
  released: "메시지 키 전달",
  handoff_claimed: "메시지 키 수령",
  completed: "완료 보고",
  expired: "만료",
};

export const ACTOR_LABELS: Record<string, string> = {
  hospital: "병원",
  operator: "hereby",
  system: "시스템",
};

// ───────────────────────── 요청용 키 보관(IndexedDB) ─────────────────────────

const DB_NAME = "hereby-chat-recovery";
const STORE = "request-keys";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

function idbRequest<T>(run: (store: IDBObjectStore) => IDBRequest<T>, mode: IDBTransactionMode) {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
        tx.oncomplete = () => db.close();
      }),
  );
}

async function loadRequestKey(requestId: string): Promise<CryptoKeyPair | undefined> {
  return idbRequest<CryptoKeyPair | undefined>((s) => s.get(requestId), "readonly");
}

async function saveRequestKey(requestId: string, pair: CryptoKeyPair): Promise<void> {
  await idbRequest((s) => s.put(pair, requestId), "readwrite");
}

async function deleteRequestKey(requestId: string): Promise<void> {
  await idbRequest((s) => s.delete(requestId), "readwrite");
}

/** 요청용 ECDH 쌍 — 비밀키는 추출 불가. 기기 승인 쌍(generateDeviceLinkKeyPair)은 추출 가능이라 쓰지 않는다. */
async function generateRequestKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, [
    "deriveBits",
  ]) as Promise<CryptoKeyPair>;
}

// ───────────────────────── 순수 도우미(테스트 대상) ─────────────────────────

/**
 * 사용자에게 보일 오류 문구 — 서버·절차 오류(TRPCError 등)는 메시지를 그대로, 화면 코드의 결함(TypeError: null/undefined
 * 접근 등)은 사용자에게 뜻이 없으니 콘솔에 남기고 fallback 을 쓴다(MED-982: 완료 뒤 화면에 「null/undefined」 오류가 떴다).
 */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof TypeError || err instanceof RangeError || err instanceof ReferenceError) {
    // biome-ignore lint/suspicious/noConsole: 클라이언트 로그(CS 리포트 수집) — 화면 결함은 사용자 문구 대신 여기 남긴다
    console.warn("[chat-recovery] client defect", err);
    return fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

function toIso(v: string | Date | null | undefined): string | null {
  if (!v) {
    return null;
  }
  return typeof v === "string" ? v : v.toISOString();
}

/** 날짜 입력(YYYY-MM-DD)을 범위 시각으로 — 끝은 그날 23:59:59.999(로컬) */
export function dateInputToIso(value: string, endOfDay: boolean): string | undefined {
  if (!value) {
    return undefined;
  }
  const d = new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** 내보내기 묶음 — 키는 넣지 않는다. 같은 입력이면 같은 바이트(해시가 보고 값이라 결정적이어야 한다). */
export function buildExportBundle(params: {
  requestId: string;
  reasonKind: string;
  reasonText: string;
  exportedAt: string;
  messages: RecoveredMessage[];
}): string {
  return JSON.stringify(
    {
      format: "hereby-chat-recovery-export",
      version: 1,
      requestId: params.requestId,
      reasonKind: params.reasonKind,
      reasonText: params.reasonText,
      exportedAt: params.exportedAt,
      messageCount: params.messages.length,
      messages: params.messages.map((m) => ({
        id: m.id,
        conversationId: m.conversationId,
        conversationTitle: m.conversationTitle,
        senderId: m.senderId,
        senderName: m.senderName,
        senderNumber: m.senderNumber,
        createdAt: m.createdAt,
        deletedAt: m.deletedAt,
        text: m.text,
        error: m.error,
        attachments: m.attachments.map((a) => ({
          name: a.name,
          mime: a.mime,
          size: a.size,
          storagePath: a.storagePath,
        })),
      })),
    },
    null,
    2,
  );
}

/** 암호문 한 줄을 푼다 — 키가 없거나 AAD(조직·방·보낸이·id·세대)가 안 맞으면 error 로 남긴다 */
export async function decryptRow(
  row: CipherRow,
  key: Uint8Array | undefined,
  organizationId: string,
): Promise<RecoveredMessage> {
  const base: RecoveredMessage = {
    id: row.id,
    conversationId: row.conversationId,
    conversationTitle: row.conversationTitle,
    senderId: row.senderId,
    senderName: row.senderName,
    senderNumber: row.senderNumber,
    createdAt: toIso(row.createdAt) ?? "",
    deletedAt: toIso(row.deletedAt),
    text: null,
    error: null,
    attachments: [],
  };
  if (!row.ciphertext) {
    return { ...base, error: "암호화되지 않은 메시지예요." };
  }
  if (!key) {
    return { ...base, error: "전달된 메시지 키에 없어요." };
  }
  try {
    const payload = await decryptMessage(
      key,
      row.ciphertext,
      buildMessageAad({
        organizationId,
        conversationId: row.conversationId,
        senderId: row.senderId ?? "",
        messageId: row.id,
        keyEpoch: row.keyEpoch ?? 0,
      }),
    );
    return { ...base, text: payload.text ?? "", attachments: payload.attachments ?? [] };
  } catch (err) {
    return { ...base, error: errorMessage(err, "메시지를 풀 수 없어요.") };
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 풀린 메시지 키 — 반응형 상태 밖(모듈 변수)에 둔다. Alpine 프록시를 거치지 않고, 화면을 닫으면 0 으로 덮는다. */
let activeKeys: Map<string, Uint8Array> | null = null;

function emptyCreateForm() {
  return {
    reasonKind: ChatRecoveryReasonKind.LEGAL_REQUEST as string,
    reasonText: "",
    documents: [] as ChatRecoveryDocument[],
    scopeMode: "rooms" as "employees" | "rooms",
    employeeSearch: "",
    employeeCandidates: [] as EmployeeCandidate[],
    employees: [] as EmployeeCandidate[],
    rooms: [] as RecoveryConversationSummary[],
    roomsLoading: false,
    roomsLoaded: false,
    selectedRoomIds: [] as string[],
    from: "",
    to: "",
  };
}

function emptyCompleteForm() {
  return {
    notified: "yes" as "yes" | "no",
    date: "",
    method: "",
    memo: "",
    reason: "",
  };
}

export function chatRecoveryPage() {
  return {
    loadedOnce: false,
    loading: false,
    loadError: "" as string,
    rows: [] as ChatRecoveryRow[],
    filter: { status: "" as string },
    busyId: "" as string,
    /** 화면 — 목록 / 복호 작업 */
    view: "list" as "list" | "work",

    createOpen: false,
    creating: false,
    uploading: false,
    form: emptyCreateForm(),
    searchTimer: 0 as ReturnType<typeof setTimeout> | 0,
    /** 제출 전 미리보기 — 이 범위로 고정될 방·메시지 */
    preview: null as null | {
      messageCount: number;
      conversationCount: number;
      conversations: RecoveryConversationSummary[];
    },
    previewLoading: false,
    previewError: "" as string,
    previewTimer: 0 as ReturnType<typeof setTimeout> | 0,
    previewSeq: 0,

    detailRow: null as ChatRecoveryRow | null,
    detail: null as ChatRecoveryRow | null,
    audit: [] as ChatRecoveryAuditRow[],
    detailLoading: false,

    work: null as WorkState | null,
    exporting: false,
    downloadingPath: "" as string,
    completeOpen: false,
    completing: false,
    completeForm: emptyCompleteForm(),

    statusLabel: (s: string) => STATUS_LABELS[s] ?? s,
    recoveryProgressSteps,
    statusTone: (s: string) => STATUS_TONES[s] ?? "ds-label--gray",
    reasonLabel: (k: string) => REASON_LABELS[k] ?? k,
    stepLabel: (s: string) => STEP_LABELS[s] ?? s,
    actorLabel: (a: string) => ACTOR_LABELS[a] ?? a,

    get sdk(): HerebyClient {
      return Alpine.store("hereby").sdk as HerebyClient;
    },
    get currentUserId(): string {
      const user = Alpine.store("hereby").currentUser as { id?: string } | null;
      return user?.id ?? "";
    },
    /** 서버와 같은 기준 — 대화 복구 권한(chatRecovery:manage.all, 시스템 관리자는 통과) */
    get canSeePage(): boolean {
      return Alpine.store("hereby").hasPermission("chatRecovery:manage");
    },
    get statusOptions(): Array<{ value: string; label: string }> {
      return Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));
    },
    get isLegalForm(): boolean {
      return this.form.reasonKind === ChatRecoveryReasonKind.LEGAL_REQUEST;
    },
    fmtDate(v: string | Date | null | undefined): string {
      return v ? (Alpine.store("hereby").formatDate(v as string) as string) : "-";
    },
    fmtDateTime(v: string | Date | null | undefined): string {
      return v ? (Alpine.store("hereby").formatDateTime(v as string) as string) : "-";
    },
    fmtBytes(n: number): string {
      if (n < 1024) {
        return `${n}B`;
      }
      if (n < 1024 * 1024) {
        return `${(n / 1024).toFixed(1)}KB`;
      }
      return `${(n / 1024 / 1024).toFixed(1)}MB`;
    },
    conversationLabel,
    typeLabel: (t: string) => CONVERSATION_TYPE_LABELS[t] ?? t,
    /** 범위 한 줄 — 「홍길동·김간호의 대화방 2개 · 2026-01-01 ~ 2026-03-31 · 메시지 120건」 */
    scopeLine(row: ChatRecoveryRow): string {
      const parts: string[] = [];
      const who = row.subjectNames?.length ? `${row.subjectNames.join("·")}의 ` : "";
      if (row.scope.conversationIds?.length) {
        parts.push(`${who}대화방 ${row.scope.conversationIds.length}개`);
      } else if (row.scope.employeeIds?.length) {
        parts.push(`${who || `직원 ${row.scope.employeeIds.length}명의 `}모든 대화`);
      }
      if (row.scope.from || row.scope.to) {
        parts.push(`${this.fmtDate(row.scope.from)} ~ ${this.fmtDate(row.scope.to)}`);
      }
      if (row.messageCountInScope !== null) {
        parts.push(`메시지 ${row.messageCountInScope}건`);
      }
      return parts.join(" · ") || "-";
    },
    /** 다음에 할 일 — 줄마다 주 동작 하나 */
    nextAction(row: ChatRecoveryRow): "approve" | "work" | "waiting" | "none" {
      if (row.status === ChatRecoveryStatus.REQUESTED) {
        return row.requesterId === this.currentUserId ? "waiting" : "approve";
      }
      if (row.status === ChatRecoveryStatus.RELEASED) {
        return row.handoffReady ? "work" : "none";
      }
      if (
        row.status === ChatRecoveryStatus.HOSPITAL_APPROVED ||
        row.status === ChatRecoveryStatus.OPERATOR_APPROVED
      ) {
        return "waiting";
      }
      return "none";
    },
    canReject(row: ChatRecoveryRow): boolean {
      return (
        row.status === ChatRecoveryStatus.REQUESTED ||
        row.status === ChatRecoveryStatus.HOSPITAL_APPROVED
      );
    },
    notificationLine(n: Record<string, unknown> | null): string {
      if (!n) {
        return "-";
      }
      if (n.notified) {
        return `고지함 · ${this.fmtDate(n.date as string)} · ${String(n.method ?? "")}${n.memo ? ` (${String(n.memo)})` : ""}`;
      }
      return `고지 안 함 · ${String(n.reason ?? "")}`;
    },

    // ── 목록 ──

    async refresh(): Promise<void> {
      this.loadedOnce = true;
      if (!this.canSeePage) {
        this.rows = [];
        return;
      }
      this.loading = true;
      this.loadError = "";
      try {
        const rows = await this.sdk.chatRecovery.list(this.filter.status || undefined, 100);
        this.rows = rows as unknown as ChatRecoveryRow[];
      } catch (err) {
        this.loadError = errorMessage(err, "");
      } finally {
        this.loading = false;
      }
    },

    // ── 상세 ──

    async openDetail(row: ChatRecoveryRow) {
      this.detailRow = row;
      this.detail = null;
      this.audit = [];
      this.detailLoading = true;
      try {
        const [detail, audit] = await Promise.all([
          this.sdk.chatRecovery.get(row.id),
          this.sdk.chatRecovery.audit(row.id),
        ]);
        this.detail = { ...row, ...(detail as unknown as ChatRecoveryRow) };
        this.audit = audit as unknown as ChatRecoveryAuditRow[];
      } catch (err) {
        showError(errorMessage(err, "상세를 불러오지 못했어요."));
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetail() {
      this.detailRow = null;
      this.detail = null;
      this.audit = [];
    },
    /** 근거 문서 열람 — 서버가 감사 행을 남기고 짧은 URL 을 준다 */
    async openDocument(row: ChatRecoveryRow, doc: ChatRecoveryDocument) {
      try {
        const { url } = await this.sdk.chatRecovery.documentUrl(row.id, doc.storagePath);
        window.open(url, "_blank", "noopener");
      } catch (err) {
        showError(errorMessage(err, "근거 문서를 열지 못했어요."));
      }
    },

    // ── 승인 · 반려 ──

    async approve(row: ChatRecoveryRow) {
      const ok = await askConfirm({
        message:
          "이 복구 요청을 승인할까요?\n승인하면 범위의 메시지 목록이 고정되고 hereby 운영자 2인 심사로 넘어가요. 승인 뒤 7일 안에 복호를 마쳐야 해요.",
        confirmLabel: "승인",
      });
      if (!ok) {
        return;
      }
      this.busyId = row.id;
      try {
        const res = await this.sdk.chatRecovery.approve(row.id);
        await this.refresh();
        showAlert(
          `승인했어요. 범위 메시지 ${res?.messageCount ?? 0}건이 hereby 심사로 넘어갔어요.`,
        );
      } catch (err) {
        showError(errorMessage(err, "승인하지 못했어요."));
      } finally {
        this.busyId = "";
      }
    },
    async reject(row: ChatRecoveryRow) {
      const reason = await showPrompt({
        title: "복구 요청 반려",
        message: "반려 사유를 적어 주세요. 감사 기록에 남아요.",
        placeholder: "예: 요청 범위가 공문과 달라요",
      });
      if (!reason?.trim()) {
        return;
      }
      this.busyId = row.id;
      try {
        await this.sdk.chatRecovery.reject(row.id, reason.trim());
        if (this.detailRow?.id === row.id) {
          this.closeDetail();
        }
        await this.refresh();
      } catch (err) {
        showError(errorMessage(err, "반려하지 못했어요."));
      } finally {
        this.busyId = "";
      }
    },

    // ── 요청 만들기 ──

    openCreate() {
      this.form = emptyCreateForm();
      this.preview = null;
      this.previewError = "";
      this.createOpen = true;
    },
    /** 사유 종류가 바뀌면 — 점검은 대화방 고르기(1개)만 */
    onReasonKindChange() {
      if (!this.isLegalForm) {
        this.form.scopeMode = "rooms";
      }
      this.schedulePreview();
    },
    onScopeModeChange() {
      this.form.employees = [];
      this.form.employeeCandidates = [];
      this.form.employeeSearch = "";
      this.resetRooms();
      this.preview = null;
      this.previewError = "";
    },
    resetRooms() {
      this.form.rooms = [];
      this.form.roomsLoaded = false;
      this.form.selectedRoomIds = [];
    },
    get subjectIds(): string[] {
      return this.form.employees.map((e: EmployeeCandidate) => e.id);
    },
    /** 당사자의 대화방 불러오기 — 조회 자체가 감사에 남는다(서버) */
    async loadRooms() {
      const ids = this.subjectIds;
      if (ids.length === 0 || ids.length > 2) {
        showError("당사자를 1~2명 골라 주세요.");
        return;
      }
      this.form.roomsLoading = true;
      try {
        const res = await this.sdk.chatRecovery.conversationsOf(ids, {
          from: dateInputToIso(this.form.from, false),
          to: dateInputToIso(this.form.to, true),
        });
        this.form.rooms = res.conversations as unknown as RecoveryConversationSummary[];
        this.form.roomsLoaded = true;
        this.form.selectedRoomIds = this.form.selectedRoomIds.filter((id: string) =>
          this.form.rooms.some((r: RecoveryConversationSummary) => r.conversationId === id),
        );
        this.schedulePreview();
      } catch (err) {
        showError(errorMessage(err, "대화방 목록을 불러오지 못했어요."));
      } finally {
        this.form.roomsLoading = false;
      }
    },
    isRoomSelected(id: string): boolean {
      return this.form.selectedRoomIds.includes(id);
    },
    toggleRoom(id: string) {
      if (this.isRoomSelected(id)) {
        this.form.selectedRoomIds = this.form.selectedRoomIds.filter((x: string) => x !== id);
      } else if (this.isLegalForm) {
        this.form.selectedRoomIds = [...this.form.selectedRoomIds, id];
      } else {
        // 점검은 1개만 — 고르면 앞 선택을 바꾼다
        this.form.selectedRoomIds = [id];
      }
      this.schedulePreview();
    },
    selectAllRooms(on: boolean) {
      this.form.selectedRoomIds = on
        ? this.form.rooms
            .filter((r: RecoveryConversationSummary) => r.messageCount > 0)
            .map((r: RecoveryConversationSummary) => r.conversationId)
        : [];
      this.schedulePreview();
    },
    /** 미리보기 — 범위가 바뀌고 0.4초 뒤 서버에 묻는다. 늦게 온 옛 응답은 버린다 */
    schedulePreview() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
      }
      this.previewTimer = setTimeout(() => this.runPreview(), 400);
    },
    async runPreview() {
      const built = buildRecoveryScope(this.form, this.isLegalForm);
      if (typeof built === "string") {
        this.preview = null;
        this.previewError = "";
        return;
      }
      const seq = ++this.previewSeq;
      this.previewLoading = true;
      this.previewError = "";
      try {
        const res = await this.sdk.chatRecovery.previewScope(built);
        if (seq !== this.previewSeq) {
          return;
        }
        this.preview = res as unknown as typeof this.preview;
      } catch (err) {
        if (seq === this.previewSeq) {
          this.preview = null;
          this.previewError = errorMessage(err, "미리보기를 불러오지 못했어요.");
        }
      } finally {
        if (seq === this.previewSeq) {
          this.previewLoading = false;
        }
      }
    },
    closeCreate() {
      if (this.creating || this.uploading) {
        return;
      }
      this.createOpen = false;
    },
    onEmployeeSearchInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => this.searchEmployees(), 250);
    },
    async searchEmployees() {
      const q = this.form.employeeSearch.trim();
      if (!q) {
        this.form.employeeCandidates = [];
        return;
      }
      try {
        const res = (await this.sdk.employee.getEmployees({
          search: q,
          page: 1,
          limit: 10,
        } as never)) as unknown as {
          items?: Array<{
            id: string;
            firstName: string;
            lastName: string;
            employeeNumber: string;
          }>;
        };
        const chosen = new Set(this.form.employees.map((e) => e.id));
        this.form.employeeCandidates = (res.items ?? [])
          .filter((e) => !chosen.has(e.id))
          .map((e) => ({
            id: e.id,
            name: `${e.lastName || ""}${e.firstName || ""}`.trim(),
            employeeNumber: e.employeeNumber,
          }));
      } catch {
        this.form.employeeCandidates = [];
      }
    },
    addEmployee(e: EmployeeCandidate) {
      const max = this.form.scopeMode === "rooms" ? 2 : 50;
      if (this.form.employees.length >= max || this.form.employees.some((x) => x.id === e.id)) {
        if (this.form.scopeMode === "rooms") {
          showError("대화방 고르기는 당사자 2명까지예요.");
        }
        return;
      }
      this.form.employees.push(e);
      this.form.employeeCandidates = this.form.employeeCandidates.filter((c) => c.id !== e.id);
      this.form.employeeSearch = "";
      this.resetRooms();
      this.schedulePreview();
    },
    removeEmployee(id: string) {
      this.form.employees = this.form.employees.filter((e) => e.id !== id);
      this.resetRooms();
      this.schedulePreview();
    },
    /** 근거 문서 — 바이트의 SHA-256 을 브라우저에서 재고 올린다. 서버가 객체를 읽어 같은 값인지 대조한다 */
    async addDocuments(event: Event) {
      const input = event.target as HTMLInputElement;
      const files = Array.from(input.files ?? []);
      input.value = "";
      if (files.length === 0) {
        return;
      }
      if (this.form.documents.length + files.length > 10) {
        showError("근거 문서는 10개까지 붙일 수 있어요.");
        return;
      }
      this.uploading = true;
      try {
        for (const file of files) {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const sha256 = await sha256Hex(bytes);
          const { key } = await this.sdk.fileStorage.uploadFile(file, "chat-recovery");
          this.form.documents.push({ storagePath: key, name: file.name, size: file.size, sha256 });
        }
      } catch (err) {
        showError(errorMessage(err, "근거 문서를 올리지 못했어요."));
      } finally {
        this.uploading = false;
      }
    },
    removeDocument(i: number) {
      this.form.documents.splice(i, 1);
    },
    async submitCreate() {
      const f = this.form;
      if (!f.reasonText.trim()) {
        showError("요청 사유를 적어 주세요.");
        return;
      }
      if (this.isLegalForm && f.documents.length === 0) {
        showError("법원·수사기관 요청은 기관 공문을 붙여야 해요.");
        return;
      }
      const built = buildRecoveryScope(f, this.isLegalForm);
      if (typeof built === "string") {
        showError(built);
        return;
      }
      const scope = built;
      if (this.preview && this.preview.messageCount === 0) {
        showError("이 범위에는 복구할 암호화 메시지가 없어요. 기간이나 대상을 바꿔 주세요.");
        return;
      }
      this.creating = true;
      let created: ChatRecoveryRow | null = null;
      let pair: CryptoKeyPair | null = null;
      try {
        pair = await generateRequestKeyPair();
        const recipientPublicKey = await exportPublicKeySpki(pair.publicKey);
        created = (await this.sdk.chatRecovery.create({
          reasonKind: f.reasonKind as ChatRecoveryReasonKind,
          reasonText: f.reasonText.trim(),
          documents: f.documents,
          scope,
          recipientPublicKey,
        })) as unknown as ChatRecoveryRow;
        await saveRequestKey(created.id, pair);
      } catch (err) {
        if (created) {
          // 요청은 만들어졌는데 키를 못 남겼다 — 이 브라우저로는 못 푼다. 사용자에게 바로 알린다
          showError(
            "요청은 만들어졌지만 이 브라우저에 복호 키를 저장하지 못했어요. 이 요청은 반려하고 다시 만들어 주세요.",
          );
        } else {
          showError(errorMessage(err, "복구 요청을 만들지 못했어요."));
        }
        this.creating = false;
        return;
      }
      this.creating = false;
      this.createOpen = false;
      await this.refresh();
      showAlert(
        "복구 요청을 만들었어요. 다른 시스템 관리자가 승인하면 hereby 운영자 심사로 넘어가요.\n풀린 메시지는 이 브라우저에서만 열 수 있어요 — 브라우저 데이터를 지우지 마세요.",
      );
    },

    // ── 복호 작업 ──

    /** handoff 수령 → 암호문 내려받기 → 메모리에서 복호. 요청을 만든 브라우저(같은 지문)만 가능 */
    async startWork(row: ChatRecoveryRow) {
      let pair: CryptoKeyPair | undefined;
      try {
        pair = await loadRequestKey(row.id);
      } catch {
        pair = undefined;
      }
      if (!pair) {
        showError(
          "이 요청의 복호 키가 이 브라우저에 없어요. 요청을 만든 브라우저에서 열어 주세요. 그 브라우저 데이터가 지워졌다면 요청을 다시 만들어야 해요.",
        );
        return;
      }
      const fingerprint = await fingerprintPublicKey(await exportPublicKeySpki(pair.publicKey));
      if (fingerprint !== row.recipientFingerprint) {
        showError("저장된 키가 요청의 수신 키와 달라요. 요청을 만든 브라우저에서 열어 주세요.");
        return;
      }
      const ok = await askConfirm({
        message:
          "메시지 키는 한 번만 받을 수 있어요. 받은 뒤 이 화면을 닫으면 풀린 내용은 사라지고 다시 받을 수 없어요.\n지금 받아서 복호를 시작할까요?",
        confirmLabel: "시작",
      });
      if (!ok) {
        return;
      }
      this.busyId = row.id;
      try {
        const claimed = await this.sdk.chatRecovery.claimHandoff(row.id);
        const keys = await openRecoveryHandoff({
          recipientPrivateKey: pair.privateKey,
          envelope: claimed.handoff,
          requestId: row.id,
        });
        activeKeys = new Map(keys.map((k) => [k.messageId, k.key]));
        const organizationId =
          ((Alpine.store("hereby").currentUser as { organizationId?: string } | null)
            ?.organizationId as string) || "";
        this.work = {
          request: row,
          organizationId,
          messages: [],
          total: claimed.messageIds.length,
          loaded: 0,
          decrypted: 0,
          failed: 0,
          loading: true,
          exportHash: null,
          exportedAt: null,
        };
        this.view = "work";
        await this.loadWorkMessages();
      } catch (err) {
        showError(errorMessage(err, "메시지 키를 받지 못했어요."));
        this.work = null;
        this.view = "list";
      } finally {
        this.busyId = "";
        this.refresh();
      }
    },
    async loadWorkMessages() {
      const work = this.work;
      if (!work) {
        return;
      }
      const limit = 200;
      try {
        for (let page = 1; ; page++) {
          const res = await this.sdk.chatRecovery.messages(work.request.id, page, limit);
          if (!this.work || this.work !== work) {
            return;
          }
          const organizationId = res.organizationId || work.organizationId;
          work.total = res.total;
          for (const item of res.items as unknown as CipherRow[]) {
            const out = await decryptRow(item, activeKeys?.get(item.id), organizationId);
            work.messages.push(out);
            work.loaded += 1;
            if (out.error) {
              work.failed += 1;
            } else {
              work.decrypted += 1;
            }
          }
          if (res.items.length < limit || work.loaded >= work.total) {
            break;
          }
        }
      } catch (err) {
        showError(errorMessage(err, "메시지를 불러오지 못했어요."));
      } finally {
        work.loading = false;
      }
    },
    /** 첨부 — URL 은 서버(범위 검사), 바이트는 본문 안 파일 키로 이 브라우저가 푼다 */
    async downloadAttachment(att: EncryptedAttachmentRef) {
      const work = this.work;
      if (!work || this.downloadingPath) {
        return;
      }
      this.downloadingPath = att.storagePath;
      try {
        const { url } = await this.sdk.chatRecovery.attachmentUrl(work.request.id, att.storagePath);
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`첨부를 내려받지 못했어요. (${res.status})`);
        }
        const bytes = new Uint8Array(await res.arrayBuffer());
        const plain = await decryptFile({
          key: fromBase64Url(att.fileKey),
          iv: att.fileIv,
          ciphertext: bytes,
        });
        downloadBlob(
          new Blob([plain as BlobPart], { type: att.mime || "application/octet-stream" }),
          att.name || "attachment",
        );
      } catch (err) {
        showError(errorMessage(err, "첨부를 풀지 못했어요."));
      } finally {
        this.downloadingPath = "";
      }
    },
    /** 내보내기 — JSON 한 파일. 해시(SHA-256)는 완료 보고에 남아 나중에 같은 파일인지 대조한다 */
    async exportBundle() {
      const work = this.work;
      if (!work || work.loading) {
        return;
      }
      this.exporting = true;
      try {
        const exportedAt = work.exportedAt ?? new Date().toISOString();
        const text = buildExportBundle({
          requestId: work.request.id,
          reasonKind: work.request.reasonKind,
          reasonText: work.request.reasonText,
          exportedAt,
          messages: work.messages,
        });
        const bytes = new TextEncoder().encode(text);
        const hash = await sha256Hex(bytes);
        work.exportedAt = exportedAt;
        work.exportHash = hash;
        downloadBlob(
          new Blob([bytes as BlobPart], { type: "application/json" }),
          `chat-recovery-${work.request.id.slice(0, 8)}-${exportedAt.slice(0, 10)}.json`,
        );
      } catch (err) {
        showError(errorMessage(err, "내보내지 못했어요."));
      } finally {
        this.exporting = false;
      }
    },
    get workIsLegal(): boolean {
      return this.work?.request.reasonKind === ChatRecoveryReasonKind.LEGAL_REQUEST;
    },
    get canComplete(): boolean {
      const work = this.work;
      if (!work || work.loading) {
        return false;
      }
      return this.workIsLegal ? Boolean(work.exportHash) : true;
    },
    openComplete() {
      if (!this.canComplete) {
        showError(
          this.workIsLegal
            ? "법적 요청은 내보내기를 한 뒤에 완료할 수 있어요."
            : "메시지를 다 불러온 뒤에 완료할 수 있어요.",
        );
        return;
      }
      this.completeForm = emptyCompleteForm();
      this.completeOpen = true;
    },
    async submitComplete() {
      const work = this.work;
      if (!work) {
        return;
      }
      const f = this.completeForm;
      let notification: ChatRecoveryNotificationInput;
      if (f.notified === "yes") {
        if (!(f.date && f.method.trim())) {
          showError("고지한 날짜와 방법을 적어 주세요.");
          return;
        }
        notification = {
          notified: true,
          date: dateInputToIso(f.date, false) ?? "",
          method: f.method.trim(),
          memo: f.memo.trim() || undefined,
        };
      } else {
        if (!f.reason.trim()) {
          showError("고지하지 않은 사유를 적어 주세요.");
          return;
        }
        notification = { notified: false, reason: f.reason.trim() };
      }
      this.completing = true;
      try {
        await this.sdk.chatRecovery.complete({
          requestId: work.request.id,
          exportHash: this.workIsLegal ? (work.exportHash ?? undefined) : undefined,
          messageCount: work.total,
          decryptedCount: work.decrypted,
          notification,
        });
      } catch (err) {
        showError(errorMessage(err, "완료 보고를 남기지 못했어요."));
        this.completing = false;
        return;
      }
      // 서버는 완료 처리됐다 — 아래는 화면 정리뿐이라 어느 하나가 실패해도 「완료」로 보여야 한다(MED-982: 내보내기 없이
      // 완료했을 때 이 뒤에서 난 오류가 「완료 보고를 남기지 못했어요」처럼 떠서 새로고침해야 완료 상태가 보였다).
      this.completing = false;
      this.completeOpen = false;
      try {
        await deleteRequestKey(work.request.id);
      } catch {
        // 키 정리는 실패해도 절차에 영향 없음(handoff 는 이미 소진)
      }
      try {
        this.clearWork();
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: 클라이언트 로그(CS 리포트 수집)
        console.warn("[chat-recovery] clearWork after complete failed", err);
        this.work = null;
        this.view = "list";
      }
      await this.refresh();
      showAlert("완료 보고를 남겼어요. 감사 기록에서 전 과정을 볼 수 있어요.");
    },
    async closeWork() {
      if (this.work && this.work.request.status !== ChatRecoveryStatus.COMPLETED) {
        const ok = await askConfirm({
          message:
            "아직 완료 보고를 하지 않았어요. 화면을 닫으면 풀린 메시지와 키가 사라지고 다시 받을 수 없어요.\n그래도 닫을까요?",
          confirmLabel: "닫기",
          tone: "danger",
        });
        if (!ok) {
          return;
        }
      }
      this.clearWork();
    },
    clearWork() {
      if (activeKeys) {
        for (const key of activeKeys.values()) {
          key.fill(0);
        }
        activeKeys.clear();
        activeKeys = null;
      }
      if (this.work) {
        this.work.messages = [];
      }
      this.work = null;
      this.view = "list";
    },
  };
}
